"use client"

import { APP_URL, BOOK_DEMO_URL } from "../../config"
import type { FullTranslation } from "../../lib/i18n"
import { useTr } from "../../lib/use-tr"
import { DeferredHighlighter } from "../deferred-highlighter"
import { LocaleLink } from "../locale-link"
import { Reveal } from "../reveal"
import {
  ArrowRight,
  CalendarClock,
  Clock,
  Gift,
  ShieldCheck,
  Sparkles,
} from "lucide-react"

import { Button } from "@/components/ui/button"

// Solid brand orange for the hand-drawn headline underline.
const HIGHLIGHT_COLOR = "oklch(0.5741 0.1375 39.0427)"

const HERO_T = {
  eyebrow: {
    FR: "Campagnes de démonstration, sans carte",
    EN: "Live demo campaigns, no card required",
  },
  headline_prefix: {
    FR: "L'agent IA qui trouve,",
    EN: "The AI agent that finds,",
  },
  headline_highlight_1: {
    FR: "qualifie",
    EN: "qualifies",
  },
  headline_mid: { FR: "et", EN: "and" },
  headline_highlight_2: {
    FR: "engage",
    EN: "engages",
  },
  headline_suffix: {
    FR: "vos prochains clients B2B.",
    EN: "your next B2B clients.",
  },
  subhead: {
    FR: "N'importe quel métier. N'importe quelle ville dans le monde. Enrichis, vérifiés, prêts à recevoir un email.",
    EN: "Any industry. Any city in the world. Enriched, verified, ready to be emailed.",
  },
  cta_demo: { FR: "Réserver une démo", EN: "Book a demo" },
  cta_try: { FR: "Créer un compte", EN: "Create an account" },
  proof_free: {
    FR: "Démos en accès libre, sans CB",
    EN: "Demo campaigns, no card",
  },
  proof_coverage: {
    FR: "Couverture mondiale",
    EN: "Worldwide coverage",
  },
  proof_carry: {
    FR: "Résiliation à tout moment",
    EN: "Cancel anytime",
  },
} satisfies FullTranslation

const PROOF_BADGES = [
  { key: "proof_free", Icon: Gift },
  { key: "proof_coverage", Icon: ShieldCheck },
  { key: "proof_carry", Icon: Clock },
] as const

export function Hero() {
  const tr = useTr(HERO_T)

  // The headline is the LCP element, so it renders in its final shape from the
  // server. rough-notation loads and draws the underline once the browser is
  // idle, over the already painted text.
  const accent = (word: string) => (
    <span className="text-primary-cta">
      <DeferredHighlighter
        action="underline"
        color={HIGHLIGHT_COLOR}
        strokeWidth={3}
        underlineGap={3}
        iterations={2}
        animationDuration={700}
      >
        {word}
      </DeferredHighlighter>
    </span>
  )

  return (
    <section
      className="relative overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-20 md:pt-36 md:pb-24"
      aria-labelledby="hero-heading"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-180"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 70% 45% at 50% 0%, oklch(0.88 0.09 50 / 0.45), transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 [mask-image:radial-gradient(ellipse_at_center,black_25%,transparent_70%)] opacity-[0.12]"
        style={{
          backgroundImage:
            "linear-gradient(to right, oklch(0.5 0 0 / 0.4) 1px, transparent 1px), linear-gradient(to bottom, oklch(0.5 0 0 / 0.4) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      <div className="relative sm:container">
        <div className="mx-auto max-w-3xl text-center">
          <Reveal immediate>
            <div className="bg-card/60 text-muted-foreground mb-6 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium backdrop-blur-sm">
              <Sparkles className="text-primary-cta size-3.5" aria-hidden />
              <span>{tr("eyebrow")}</span>
            </div>
          </Reveal>
          <h1
            id="hero-heading"
            className="text-foreground font-display tracking-dense leading-hero lg:text-hero text-4xl font-semibold text-balance sm:text-5xl md:text-6xl"
          >
            {tr("headline_prefix")} {accent(tr("headline_highlight_1"))}{" "}
            {tr("headline_mid")} {accent(tr("headline_highlight_2"))}{" "}
            {tr("headline_suffix")}
          </h1>

          <Reveal immediate delay={0.16}>
            <p className="text-muted-foreground text-md mx-auto mt-5 max-w-xl leading-relaxed text-balance sm:text-base">
              {tr("subhead")}
            </p>
          </Reveal>

          <Reveal immediate delay={0.24}>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
              <a
                href={BOOK_DEMO_URL}
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto"
              >
                <Button
                  variant="cta"
                  shape="lg"
                  size="lg"
                  className="cta-shine cta-glow text-md h-12 w-full px-6 font-medium sm:w-auto"
                >
                  <CalendarClock className="mr-1.5 size-4" aria-hidden />
                  {tr("cta_demo")}
                </Button>
              </a>
              {/* Above the fold, so a viewport prefetch of the dynamic /login
                  document fires on every landing load. prefetch={false} still
                  warms it on hover and touchstart, which is when intent
                  actually shows. */}
              <LocaleLink
                href={APP_URL}
                prefetch={false}
                className="w-full sm:w-auto"
              >
                <Button
                  shape="lg"
                  variant="soft"
                  size="lg"
                  className="group text-md h-12 w-full px-6 font-medium sm:w-auto"
                >
                  {tr("cta_try")}
                  <ArrowRight className="ml-1.5 size-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </LocaleLink>
            </div>
          </Reveal>

          <div className="text-muted-foreground sm:text-2sm mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs">
            {PROOF_BADGES.map(({ key, Icon }, idx) => (
              <div key={key} className="flex items-center gap-4">
                {idx > 0 ? (
                  <span
                    aria-hidden
                    className="bg-border/80 hidden h-3 w-px sm:block"
                  />
                ) : null}
                <span className="flex items-center gap-1.5">
                  <Icon className="text-primary-cta/70 size-3.5" aria-hidden />
                  <span>{tr(key)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
